import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronLeft, FaChevronRight, FaStar, FaCheck } from "react-icons/fa";

// Import product images
import ProductImg1 from "../asset/Product/SVG 01.svg";
import ProductImg2 from "../asset/Product/SVG 02.svg";
import ProductImg3 from "../asset/Product/SVG 03.svg";
import ProductImg4 from "../asset/Product/SVG 04.svg";
import ProductImg5 from "../asset/Product/SVG 05.svg";

const productImages = [ProductImg1, ProductImg2, ProductImg3, ProductImg4, ProductImg5];

const ShopButton = ({ children, href, isPrimary = true }) => {
  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`relative w-full sm:w-auto px-8 py-4 rounded-2xl font-radon 
      text-lg font-semibold tracking-wide text-center overflow-hidden group
      min-w-[200px] sm:min-w-[240px]`}
      whileHover={{ 
        scale: 1.02,
        y: -2
      }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
    >
      {/* Background with glassmorphism effect */}
      <div className={`absolute inset-0 rounded-2xl backdrop-blur-sm ${
        isPrimary 
          ? 'bg-gradient-to-r from-gold/90 to-secondary/90 border border-gold/30' 
          : 'bg-gradient-to-r from-orange-500/90 to-orange-600/90 border border-orange-400/30'
      }`} />
      
      {/* Animated overlay */}
      <motion.div
        className={`absolute inset-0 rounded-2xl ${
          isPrimary 
            ? 'bg-gradient-to-r from-secondary/20 to-gold/20' 
            : 'bg-gradient-to-r from-orange-400/20 to-orange-500/20'
        }`}
        initial={{ opacity: 0 }}
        whileHover={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      />
      
      {/* Shimmer effect */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
        initial={{ x: "-100%" }}
        animate={{ x: "100%" }}
        transition={{ 
          duration: 2,
          repeat: Infinity,
          repeatDelay: 4,
          ease: "easeInOut"
        }}
      />
      
      {/* Content */}
      <span className={`relative z-10 flex items-center justify-center gap-3 ${
        isPrimary ? 'text-purple900' : 'text-white'
      }`}>
        <motion.span
          className="text-2xl"
          whileHover={{ rotate: [0, -10, 10, 0] }}
          transition={{ duration: 0.5 }}
        >
          {isPrimary ? '🛒' : '📦'}
        </motion.span>
        <span className="font-medium">{children}</span>
        <motion.span
          className="text-xl"
          whileHover={{ x: 5 }}
          transition={{ duration: 0.2 }}
        >
          →
        </motion.span>
      </span>
    </motion.a>
  );
};

export default function Products() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % productImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + productImages.length) % productImages.length);
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
    setIsAutoPlaying(false); // Stop auto-play when user manually selects
  };

  // Auto-looping functionality
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % productImages.length);
    }, 4000); // Change image every 4 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  // Resume auto-play after 10 seconds of inactivity
  useEffect(() => {
    if (!isAutoPlaying) {
      const timer = setTimeout(() => {
        setIsAutoPlaying(true);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [isAutoPlaying]);

  const benefits = [
    "4-in-1 Skincare Solution – Cleanses, removes tan, brightens skin, and helps even out skin tone for a naturally radiant look.",
    "Enriched with Natural Extracts – Infused with Kojic Acid, Licorice, Aloe Vera & Vitamin E to nourish and protect while improving skin texture.",
    "Gentle Yet Effective Cleanser – Removes dirt, oil, and impurities without stripping moisture, keeping skin soft, fresh, and glowing.",
    "Suitable for All Skin Types – Dermatologically safe, unisex formula, free from parabens, sulfates, and harsh chemicals. Ideal for teens & adults.",
    "Crafted with Care, Inspired by Nature – Proudly made with premium ingredients, offering daily skincare that supports a healthy, youthful complexion."
  ];

  const ingredients = [
    "Dm Water", "Disodium EDTA", "Sodium Lauryl Sarcocinate",
    "Coco Amido Propyl Betain", "Coco Mono Ethanol Amine",
    "PEG 150 Di Stearate", "Kojic Acid", "Licorice Extract", 
    "Aloe Vera Extract", "Fragrance", "Vitamin E", 
    "Phenoxy Ethanol", "Ethyl Hexyl Glycerin"
  ];

  return (
    <section id="products" className="relative py-20 overflow-hidden">
      {/* Premium Gradient Background */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.5 }}
        className="absolute inset-0 bg-gradient-to-b from-purple900 via-purple800 to-purple600"
      />

      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(4)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-gradient-to-r from-purple400/10 to-secondary/10"
            initial={{ 
              width: `${Math.random() * 400 + 200}px`,
              height: `${Math.random() * 400 + 200}px`,
              x: `${Math.random() * 100}%`,
              y: `${Math.random() * 100}%`,
              opacity: 0 
            }}
            animate={{
              y: ["0%", "10%", "0%"],
              opacity: [0.1, 0.3, 0.1],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 10 + i * 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        ))}
      </div>

      <div className="relative container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-radon text-gold mb-6 font-medium">
            Featured Product
          </h2>
          <p className="text-lg md:text-xl text-secondary/80 font-konnect max-w-2xl mx-auto">
            Experience the power of premium skincare with our signature face wash
          </p>
        </motion.div>

        {/* Product Showcase */}
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            
            {/* Left Column - Images and Additional Info */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative space-y-8"
            >
              {/* Product Images Slider */}
              <div className="relative">
              {/* Main Image */}
              <div className="relative bg-purple900/30 backdrop-blur-sm rounded-3xl p-6 border border-gold/20 overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentImageIndex}
                    src={productImages[currentImageIndex]}
                    alt={`KesariGlow GlowBurst Face Wash - View ${currentImageIndex + 1}`}
                    className="w-full h-80 object-contain"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.2 }}
                    transition={{ duration: 0.5 }}
                  />
                </AnimatePresence>

                {/* Navigation Arrows */}
                <button
                  onClick={() => {
                    prevImage();
                    setIsAutoPlaying(false);
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-purple900/80 backdrop-blur-sm
                    border border-gold/30 text-gold hover:bg-gold/10 hover:border-gold/50
                    transition-all duration-300 flex items-center justify-center group"
                >
                  <FaChevronLeft className="group-hover:scale-110 transition-transform duration-300" />
                </button>
                
                <button
                  onClick={() => {
                    nextImage();
                    setIsAutoPlaying(false);
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-purple900/80 backdrop-blur-sm
                    border border-gold/30 text-gold hover:bg-gold/10 hover:border-gold/50
                    transition-all duration-300 flex items-center justify-center group"
                >
                  <FaChevronRight className="group-hover:scale-110 transition-transform duration-300" />
                </button>

                {/* Image Indicators */}
                <div className="flex justify-center mt-6 space-x-2">
                  {productImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToImage(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === currentImageIndex 
                          ? 'bg-gold scale-125' 
                          : 'bg-gold/30 hover:bg-gold/50'
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Thumbnail Images */}
              <div className="flex justify-center mt-4 space-x-3">
                {productImages.map((img, index) => (
                  <motion.button
                    key={index}
                    onClick={() => goToImage(index)}
                    className={`w-14 h-14 rounded-xl overflow-hidden border-2 transition-all duration-300 ${
                      index === currentImageIndex 
                        ? 'border-gold scale-110' 
                        : 'border-gold/20 hover:border-gold/50'
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <img 
                      src={img} 
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </motion.button>
                ))}
              </div>
              </div>

              {/* How to Use - Only visible on large screens */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="hidden lg:block bg-purple900/30 backdrop-blur-sm rounded-2xl p-6 border border-gold/10"
              >
                <h4 className="text-xl font-radon text-gold mb-4">How To Use</h4>
                <ol className="space-y-3 text-purple100/90 font-konnect text-sm">
                  <li className="flex items-start space-x-3">
                    <span className="bg-gold text-purple900 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">1</span>
                    <span>Take a small amount of the face wash on your palm.</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="bg-gold text-purple900 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">2</span>
                    <span>Massage gently on face and neck in circular motions for 1 minute.</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="bg-gold text-purple900 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">3</span>
                    <span>Rinse thoroughly with clean water.</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="bg-gold text-purple900 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">4</span>
                    <span>Pat dry with a soft towel and follow with moisturizer.</span>
                  </li>
                </ol>
              </motion.div>

              {/* Ingredients - Only visible on large screens */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="hidden lg:block bg-purple900/30 backdrop-blur-sm rounded-2xl p-6 border border-gold/10"
              >
                <h4 className="text-xl font-radon text-gold mb-4">Key Ingredients</h4>
                <div className="flex flex-wrap gap-2">
                  {ingredients.map((ingredient, index) => (
                    <motion.span
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="px-3 py-1 bg-gold/10 text-gold/90 rounded-full text-xs font-konnect border border-gold/20"
                    >
                      {ingredient}
                    </motion.span>
                  ))}
                </div>
              </motion.div>
            </motion.div>

            {/* Product Details */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-6"
            >
              {/* Product Title */}
              <div>
                <h3 className="text-2xl md:text-3xl font-radon text-gold mb-4 leading-tight">
                  KesariGlow GlowBurst Face Wash
                </h3>
                <p className="text-lg text-secondary/90 font-konnect mb-4">
                  Tan Removal, Brightening & Even Skin Tone | 4-in-1 Care | Natural Ingredients | 100ml
                </p>
                <div className="flex items-center space-x-2 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <FaStar key={i} className="text-gold text-lg" />
                  ))}
                  <span className="text-purple100/70 font-konnect ml-2">Premium Quality</span>
                </div>
              </div>

              {/* Product Description */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="bg-purple900/30 backdrop-blur-sm rounded-2xl p-6 border border-gold/10"
              >
                <h4 className="text-xl font-radon text-gold mb-4">About This Product</h4>
                <p className="text-purple100/90 font-konnect leading-relaxed text-sm">
                  Reveal your natural glow with KesariGlow GlowBurst Face Wash – a daily-use cleanser designed to remove tan, brighten skin, and improve texture while keeping your skin soft and hydrated. Enriched with Kojic Acid, Licorice Extract, Aloe Vera, and Vitamin E, this herbal blend gently removes dirt, oil, and impurities without stripping away moisture. Suitable for men, women, and all skin types, GlowBurst helps you achieve a fresh, radiant, and even-toned complexion every day.
                </p>
              </motion.div>

              {/* Key Benefits */}
              <div className="bg-purple900/30 backdrop-blur-sm rounded-2xl p-6 border border-gold/10">
                <h4 className="text-xl font-radon text-gold mb-4">Key Benefits</h4>
                <ul className="space-y-3">
                  {benefits.map((benefit, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="flex items-start space-x-3 text-purple100/90 font-konnect"
                    >
                      <FaCheck className="text-gold mt-1 flex-shrink-0" />
                      <span className="text-sm leading-relaxed">{benefit}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>

              {/* Buy Now Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                {/* Amazon Button - Shop Collection Style */}
                <a
                  href="https://amzn.in/d/6znIw9W"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto bg-gradient-to-r from-gold to-secondary 
                    px-8 py-3 sm:py-4 rounded-full text-purple900 font-radon 
                    text-lg sm:text-xl font-medium tracking-wider
                    transform hover:scale-105 transition-all duration-300 
                    hover:from-secondary hover:to-gold
                    min-w-[200px] sm:min-w-[220px] text-center"
                >
                  Buy on Amazon
                </a>

                {/* Flipkart Button - Our Story Style */}
                <a
                  href="https://www.flipkart.com/kesari-glow-brust-face-wash/p/itm60c264a7fc817"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto bg-transparent border-2 border-gold/50 
                    px-8 py-3 sm:py-4 rounded-full text-gold font-radon 
                    text-lg sm:text-xl font-medium tracking-wider
                    transform hover:scale-105 transition-all duration-300 
                    hover:bg-gold/10 hover:border-gold
                    min-w-[200px] sm:min-w-[220px] text-center"
                >
                  Buy on Flipkart
                </a>
              </div>
            </motion.div>
          </div>

          {/* Additional Information Row - Only visible on mobile/tablet */}
          <div className="grid lg:grid-cols-2 gap-8 mt-12 lg:hidden">
            {/* How to Use */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-purple900/30 backdrop-blur-sm rounded-2xl p-6 border border-gold/10"
            >
              <h4 className="text-xl font-radon text-gold mb-4">How To Use</h4>
              <ol className="space-y-3 text-purple100/90 font-konnect text-sm">
                <li className="flex items-start space-x-3">
                  <span className="bg-gold text-purple900 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">1</span>
                  <span>Take a small amount of the face wash on your palm.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="bg-gold text-purple900 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">2</span>
                  <span>Massage gently on face and neck in circular motions for 1 minute.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="bg-gold text-purple900 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">3</span>
                  <span>Rinse thoroughly with clean water.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="bg-gold text-purple900 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">4</span>
                  <span>Pat dry with a soft towel and follow with moisturizer.</span>
                </li>
              </ol>
            </motion.div>

            {/* Ingredients */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-purple900/30 backdrop-blur-sm rounded-2xl p-6 border border-gold/10"
            >
              <h4 className="text-xl font-radon text-gold mb-4">Key Ingredients</h4>
              <div className="flex flex-wrap gap-2">
                {ingredients.map((ingredient, index) => (
                  <motion.span
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="px-3 py-1 bg-gold/10 text-gold/90 rounded-full text-xs font-konnect border border-gold/20"
                  >
                    {ingredient}
                  </motion.span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
} 